import { useMemo } from "react";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Search as SearchIcon, X } from "lucide-react";
import { ALL_ARTICLES, type IndexedArticle } from "@/lib/article-index";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { makeSeoDescription } from "@/lib/seo-description";

const TITLE = "Search LifeWestWay Guides | LifeWestWay";
const DESCRIPTION =
  "Search every LifeWestWay guide across the USA, UK, Australia, Italy, Portugal and Canada, or browse articles by category and country.";

export const Route = createFileRoute("/search")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: makeSeoDescription(DESCRIPTION) },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: makeSeoDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://lifewestway.com/search" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://lifewestway.com/search" }],
  }),
  component: SearchPage,
});

function Pill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
        active
          ? "border-accent bg-accent/15 text-accent"
          : "border-border bg-card/60 text-foreground/70 hover:border-accent/60 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function SearchPage() {
  // Keep search parameters entirely client-side. A route-level validator with
  // defaults can canonicalize SSR requests and emit a 307 before page content.
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const q = typeof search.q === "string" ? search.q.slice(0, 120) : "";
  const category = typeof search.category === "string" ? search.category.slice(0, 60) : "";
  const country = typeof search.country === "string" ? search.country.slice(0, 30) : "";
  const navigate = useNavigate({ from: "/search" });

  const set = (patch: Partial<{ q: string; category: string; country: string }>) => {
    const next = { q, category, country, ...patch };
    navigate({
      search: {
        q: next.q || undefined,
        category: next.category || undefined,
        country: next.country || undefined,
      },
      replace: true,
    });
  };

  const categories = useMemo(
    () => Array.from(new Set(ALL_ARTICLES.map((a) => a.category))).sort(),
    [],
  );
  const countries = useMemo(
    () => Array.from(new Set(ALL_ARTICLES.map((a) => a.countryLabel))).sort(),
    [],
  );

  const results: IndexedArticle[] = useMemo(() => {
    const term = q.trim().toLowerCase();
    return ALL_ARTICLES.filter((a) => {
      if (category && a.category !== category) return false;
      if (country && a.countryLabel !== country) return false;
      if (!term) return true;
      return `${a.title} ${a.description} ${a.category} ${a.countryLabel}`
        .toLowerCase()
        .includes(term);
    });
  }, [q, category, country]);

  const grouped = useMemo(() => {
    const map = new Map<string, IndexedArticle[]>();
    for (const a of results) {
      const list = map.get(a.category) ?? [];
      list.push(a);
      map.set(a.category, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [results]);

  const hasFilters = Boolean(q || category || country);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs items={[{ label: "Search" }]} />

      <header className="mt-4 max-w-3xl">
        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          Search LifeWestWay guides
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
          {ALL_ARTICLES.length}+ in-depth 2026 guides on visas, jobs, tax, housing, banking and
          permanent residency across six destinations. Search by keyword or browse the category
          and country shelves below.
        </p>
      </header>

      {/* Visible search field */}
      <div className="mt-6 flex items-center gap-3 rounded-2xl border border-border bg-card/70 px-4 py-3 shadow-sm focus-within:border-accent">
        <SearchIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-accent" />
        <label className="sr-only" htmlFor="site-search">
          Search articles
        </label>
        <input
          id="site-search"
          type="search"
          value={q}
          maxLength={120}
          onChange={(e) => set({ q: e.target.value })}
          placeholder="Try “skilled worker visa”, “cost of living”, “tax return”…"
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        {hasFilters && (
          <button
            type="button"
            onClick={() => set({ q: "", category: "", country: "" })}
            className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition hover:border-accent hover:text-foreground"
          >
            <X aria-hidden="true" className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {/* Category + country shelves */}
      <section aria-labelledby="filter-country" className="mt-6">
        <h2 id="filter-country" className="text-xs font-semibold uppercase tracking-widest text-accent">
          Browse by country
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <Pill active={!country} label="All countries" onClick={() => set({ country: "" })} />
          {countries.map((c) => (
            <Pill
              key={c}
              active={country === c}
              label={c}
              onClick={() => set({ country: country === c ? "" : c })}
            />
          ))}
        </div>
      </section>

      <section aria-labelledby="filter-category" className="mt-6">
        <h2 id="filter-category" className="text-xs font-semibold uppercase tracking-widest text-accent">
          Browse by category
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <Pill active={!category} label="All categories" onClick={() => set({ category: "" })} />
          {categories.map((c) => (
            <Pill
              key={c}
              active={category === c}
              label={c}
              onClick={() => set({ category: category === c ? "" : c })}
            />
          ))}
        </div>
      </section>

      {/* Results */}
      <section aria-live="polite" className="mt-10">
        <h2 className="text-lg font-semibold text-foreground">
          {results.length} {results.length === 1 ? "guide" : "guides"}
          {q ? ` matching “${q}”` : ""}
        </h2>

        {results.length === 0 && (
          <p className="mt-4 rounded-xl border border-border bg-card/60 p-6 text-sm text-muted-foreground">
            No guides matched that search. Try a broader keyword such as “visa”, “tax” or
            “housing”, or clear the filters above.
          </p>
        )}

        <div className="mt-6 space-y-10">
          {grouped.map(([cat, items]) => (
            <div key={cat}>
              <h3 className="text-sm font-semibold uppercase tracking-widest text-accent">
                {cat}
              </h3>
              <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((a) => (
                  <li key={a.href}>
                    <Link title={`Open guide: ${a.title}`}
                      to={a.href}
                      className="luxe-tile flex h-full flex-col gap-2 rounded-xl border border-border bg-card/70 p-4 transition hover:border-accent"
                    >
                      <span className="w-fit rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-accent">
                        {a.countryLabel}
                      </span>
                      <h4 className="line-clamp-2 text-sm font-semibold text-foreground">
                        {a.title}
                      </h4>
                      <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                        {a.description}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
